import {
  buildSelectedQuoteData,
  type ResumeSessionResponse,
} from "@/D2C/features/auth/utils/resumeJourney";
import { normalizeMotorQuote } from "@/D2C/features/car-quote/utils/normalizeMotorQuote";
import { LOB_IDS, pipelineMapping } from "@/shared/common/static";
import { ADD_QUOTE_PRODUCTS } from "../config/quoteProducts.config";

const POST_STEP_NAMES = new Set([
  "selected_quote",
  "step_5_post_request",
  "step_6_post_request",
  "step_7_post_request",
  "step_8_post_request",
]);

const QUOTE_LIST_STEP_NAMES = new Set(["quote_request"]);

export type QuoteViewUiState = {
  showQuoteList: boolean;
  showPostQuote: boolean;
  selectedQuote: ReturnType<typeof buildSelectedQuoteData> | null;
  isPostDataReady: boolean;
  isPolicyIssued: boolean;
  stageName: string;
  prefetchedQuotes: unknown[];
};

export function getProductFromSession(sessionData: ResumeSessionResponse) {
  const lobId = Number(sessionData?.lob_id);
  return ADD_QUOTE_PRODUCTS.find((product) => product.lobId === lobId && product.available);
}

export function mapSessionSelectedQuote(sessionData: ResumeSessionResponse) {
  const quote = buildSelectedQuoteData(sessionData?.selected_quote);
  if (!quote) return null;

  return {
    ...quote,
    quote_id: sessionData.id,
    id: sessionData.id,
  };
}

export function getSessionQuotes(sessionData: ResumeSessionResponse) {
  const quotes =
    sessionData?.quote_request?.quotes ||
    (sessionData as any)?.responses ||
    (sessionData as any)?.quote?.quote_request?.quotes ||
    (sessionData as any)?.quote?.responses ||
    (sessionData as any)?.raw_request?.quotes ||
    (sessionData as any)?.quotes ||
    [];

  const normalizedQuotes = Array.isArray(quotes) ? quotes.filter(Boolean) : [];

  if (Number(sessionData?.lob_id) === LOB_IDS.MOTOR) {
    return normalizedQuotes
      .filter((quote: any) => quote?.insurer?.name && quote?.plan?.name)
      .map((quote: any) => normalizeMotorQuote(quote));
  }

  return normalizedQuotes;
}

export function getQuoteViewUiState(sessionData: ResumeSessionResponse): QuoteViewUiState {
  const stepName = sessionData?.current_step_name || "";
  const hasSelectedQuote = Boolean(sessionData?.selected_quote);
  const isPolicyIssued = stepName === "step_8_post_request";

  const showPostQuote =
    POST_STEP_NAMES.has(stepName) && (stepName !== "selected_quote" || hasSelectedQuote);

  const showQuoteList = QUOTE_LIST_STEP_NAMES.has(stepName) && !hasSelectedQuote;

  const stageName =
    pipelineMapping.travel?.[stepName as keyof typeof pipelineMapping.travel] ||
    stepName ||
    "New Quote";

  const isPostDataReady =
    stepName === "step_6_post_request" ||
    stepName === "step_7_post_request" ||
    stepName === "step_8_post_request";

  return {
    showQuoteList,
    showPostQuote,
    selectedQuote: showPostQuote ? mapSessionSelectedQuote(sessionData) : null,
    isPostDataReady,
    isPolicyIssued,
    stageName,
    prefetchedQuotes: getSessionQuotes(sessionData),
  };
}

export function getSessionContact(sessionData: ResumeSessionResponse) {
  const step1 = sessionData?.step_1_pre_request;
  const step2Personal = sessionData?.step_2_pre_request?.personal_details;
  const quotePersonal = sessionData?.quote_request?.personal_details;
  const client = sessionData?.client;

  const firstName =
    step1?.first_name ||
    step2Personal?.first_name ||
    quotePersonal?.first_name ||
    client?.first_name ||
    "";
  const lastName =
    step1?.last_name ||
    step2Personal?.last_name ||
    quotePersonal?.last_name ||
    client?.last_name ||
    "";

  return {
    email: step1?.email || step2Personal?.email || quotePersonal?.email || client?.email || "",
    customerName: `${firstName} ${lastName}`.trim() || "Customer",
  };
}
